/**
 * Global Socket.IO instance with Enhanced Reconnection
 * Production Hardening - Days 9-10: Error Recovery & Resilience
 * Maintains one persistent connection with automatic reconnection and error handling
 */
import io from "socket.io-client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Single global socket instance
let socket = null;
let currentUser = null;
let currentRooms = new Set(); // Track joined rooms for reconnection
let isManualDisconnect = false;

/**
 * Get or create the global socket instance with enhanced reconnection
 */
export const getSocket = () => {
  if (!socket) {
    socket = io(BACKEND_URL, {
      path: "/api/socket.io",
      transports: ["websocket"], // WebSocket-only for Railway (no sticky sessions)
      upgrade: false, // Don't attempt transport upgrade
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Connection established
    socket.on("connect", () => {
      console.log("✅ Socket connected:", socket.id);
      
      // Rejoin all rooms after reconnect (silent - no toast)
      if (currentUser) {
        console.log("🔄 Rejoining rooms after reconnect:", Array.from(currentRooms));
        currentRooms.forEach(room => {
          if (room.startsWith("league_")) {
            const leagueId = room.replace("league_", "");
            socket.emit("join_league", { leagueId });
          } else if (room.startsWith("auction_")) {
            const auctionId = room.replace("auction_", "");
            socket.emit("join_auction", { auctionId });
          }
        });
      }
    });

    // Connection error (silent - log only)
    socket.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error.message);
    });

    // Disconnection (silent - log only)
    socket.on("disconnect", (reason) => {
      console.log("❌ Socket disconnected:", reason);
    });

    // Reconnection attempt
    socket.io.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Reconnection attempt ${attemptNumber}...`);
    });

    // Reconnection failed (silent - log only)
    socket.io.on("reconnect_failed", () => {
      console.error("❌ Reconnection failed after all attempts");
    });

    // Successful reconnection
    socket.io.on("reconnect", (attemptNumber) => {
      console.log(`✅ Reconnected after ${attemptNumber} attempts`);
    });

    // Server confirmation
    socket.on("connected", (data) => {
      console.log("✅ Socket connection confirmed:", data.sid);
    });

    // Error event from server (silent - log only)
    socket.on("error", (error) => {
      console.error("❌ Socket error from server:", error);
    });
  }

  return socket;
};

/**
 * Set current user for room rejoining after reconnects
 */
export const setSocketUser = (user) => {
  currentUser = user;
  isManualDisconnect = false;
  console.log("👤 Socket user set:", user?.id);
};

/**
 * Clear socket user (on logout)
 */
export const clearSocketUser = () => {
  currentUser = null;
  currentRooms.clear();
  isManualDisconnect = true;
  console.log("👤 Socket user cleared");
};

/**
 * Join a league room
 */
export const joinLeagueRoom = (leagueId) => {
  const socket = getSocket();
  console.log("🟦 Joining league room:", leagueId);
  socket.emit("join_league", { leagueId });
  currentRooms.add(`league_${leagueId}`);
};

/**
 * Leave a league room
 */
export const leaveLeagueRoom = (leagueId) => {
  const socket = getSocket();
  console.log("🟦 Leaving league room:", leagueId);
  socket.emit("leave_league", { leagueId });
  currentRooms.delete(`league_${leagueId}`);
};

/**
 * Join an auction room
 */
export const joinAuctionRoom = (auctionId) => {
  const socket = getSocket();
  console.log("🟧 Joining auction room:", auctionId);
  socket.emit("join_auction", { auctionId });
  currentRooms.add(`auction_${auctionId}`);
};

/**
 * Leave an auction room
 */
export const leaveAuctionRoom = (auctionId) => {
  const socket = getSocket();
  console.log("🟧 Leaving auction room:", auctionId);
  // Note: No leave event needed, socket will disconnect or join other rooms
  currentRooms.delete(`auction_${auctionId}`);
};

/**
 * Check if socket is currently connected
 */
export const isSocketConnected = () => {
  return socket?.connected || false;
};

/**
 * Manually disconnect socket (for cleanup)
 */
export const disconnectSocket = () => {
  if (socket) {
    isManualDisconnect = true;
    socket.disconnect();
    socket = null;
    currentRooms.clear();
    console.log("🔌 Socket manually disconnected");
  }
};
